import { useEffect, useRef } from 'react';
import gql from 'graphql-tag';

import { useSelector, useDispatch, useLogger } from '~/Hooks';
import { useQuery } from '@apollo/client';
import { getAppliedMenus, getAppliedMenusVariables } from './__queries__';
import { setInterval } from 'timers';
import { findMenuForCurrentTime } from '~utilities';
import { DateTime } from 'luxon';

const MENU_QUERY = gql`
  query getAppliedMenus($venueId: String!, $companyId: String!) {
    getAppliedMenus(venueId: $venueId, companyId: $companyId, serviceType: [POS]) {
      results {
        id
        availabilities {
          dayOfWeek
          timeRange {
            end
            start
          }
        }

        menu {
          id
          name
          menuEntries {
            id

            price
            instant

            item {
              id
              vatRate
            }
          }
        }
      }
    }
  }
`;

const isSelfServiceActive = () =>
  window.location.hash.includes('self-service-kiosks') || window.location.hash.includes('express');

const MenuFetchingComponent = () => {
  const dispatch = useDispatch();
  const logger = useLogger('menu-fetcher');
  const currentMenuRef = useRef<string>();

  const demoMode = useSelector((state) => state.demoMode);

  let companyId = useSelector((state) => state.company);
  let venueId = useSelector((state) => state.venueId);

  if (demoMode) {
    venueId = window.DEMO_MODE_VENUE_ID;
    companyId = window.DEMO_MODE_COMPANY_ID;
  }

  const menuQuery = useQuery<getAppliedMenus, getAppliedMenusVariables>(MENU_QUERY, {
    variables: {
      venueId: venueId || '',
      companyId: companyId || '',
    },
    skip: !companyId || !venueId,
    fetchPolicy: 'network-only',
  });

  const menus = menuQuery.data?.getAppliedMenus?.results ?? [];

  const updateTimeControlledMenu = () => {
    // If we are on the EPOS screen, do nothing, otherwise EPOS will get the wrong menu from the POS
    if (isSelfServiceActive()) {
      return;
    }

    const getCorrectMenu = findMenuForCurrentTime(menus);
    const foundDefaultMenu = menus.find((menu) => menu.availabilities.length === 0);

    if (!getCorrectMenu && foundDefaultMenu) {
      logger.info('Setting default menu');
      dispatch({
        type: 'SET_POS_MENU',
        menu: foundDefaultMenu.menu,
      });
      return;
    }

    if (getCorrectMenu?.menu.id === currentMenuRef?.current) {
      // Menu is already set, returning
      return;
    } else {
      logger.info('Setting time controlled menu', {
        menu: getCorrectMenu?.menu,
        availabilities: getCorrectMenu?.availabilities,
      });

      dispatch({
        type: 'SET_POS_MENU',
        menu: getCorrectMenu?.menu ?? false,
      });

      // Update the current menu ref to keep track of the current menu easily
      currentMenuRef.current = getCorrectMenu?.menu.id;
    }
  };

  const startMinuteInterval = () => {
    // Always change the menu on a full minute
    const now = DateTime.local();
    const secondsUntilNextMinute = 60 - now.second;
    const delay = secondsUntilNextMinute * 1000;

    setTimeout(() => {
      updateTimeControlledMenu(); // Run once at the next full minute

      setInterval(() => {
        updateTimeControlledMenu();
      }, 60 * 1000); // Then run every minute
    }, delay);
  };

  useEffect(() => {
    if (!menuQuery.data?.getAppliedMenus?.results?.length) {
      return;
    }

    // Check if any of the menus for POS has availabilities
    const hasAvailabilities = menus.some((appliedMenu) => appliedMenu.availabilities.length > 0);

    // If there are no menus with availabilities, set the first menu which should also be the only menu
    if (!hasAvailabilities) {
      // Filter away all take away menus to get the "default" menu
      const posMenu = menus.filter((menu) => !menu.menu.name?.startsWith('[TA]'))[0];

      // Find a menu that starts with [TA] which means it is a take away menu
      const takeAwayMenu = menus.find((menu) => menu.menu.name?.startsWith('[TA]'));
      const hasTakeAwayMenu = takeAwayMenu && takeAwayMenu?.menu.id !== posMenu.id;

      dispatch({
        type: 'SET_POS_MENU',
        menu: posMenu.menu,
      });

      if (hasTakeAwayMenu && takeAwayMenu) {
        dispatch({
          type: 'SET_POS_TAKE_AWAY_MENU',
          menu: takeAwayMenu.menu,
        });
      } else {
        dispatch({
          type: 'SET_POS_TAKE_AWAY_MENU',
          menu: null,
        });
      }

      logger.info('Setting new menu', {
        menu: menuQuery.data.getAppliedMenus.results[0].menu,
      });
    } else {
      // Set the menu instantly
      updateTimeControlledMenu();

      // Start the interval to check for new menu every minute
      startMinuteInterval();
    }
  }, [menuQuery.data?.getAppliedMenus]);

  return null;
};

export default MenuFetchingComponent;
